import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  gravarGranulometriaPeneiras, gravarGranulometriaSedimentacao,
} from '../../api/granulometria';
import type { EnsaioGranulometria } from '../../api/granulometria';
import { BlocoDaFicha } from '../../Ficha';
import { numero, paraCampo, paraNumero } from '../../formato';
import { useSessao } from '../../sessao';
import {
  CampoNumerico, ErroDaApi, EscolhaPorCodigo, Selo, TabelaDeLancamento, comoCodigo,
} from '../../ui';
import type { ColunaDeLancamento, LinhaDeLancamento, Opcao } from '../../ui';
import {
  CASAS, CurvaGranulometrica, SITUACAO_LEITURA, TEMPOS_DA_NORMA,
  pontosAnomalos, pontosDaCurvaGranulometrica, pct, rotuloTempo,
} from '../Granulometria';
import { MolduraDaRepeticao, useRepeticao } from './Repeticao';
import type { UseRepeticao } from './Repeticao';
import { diferencaComSinal } from './RepeticaoNumerica';

/**
 * Repetir a granulometria na Interpretação (onda 323, D7: as curvas
 * granulométricas e as frações).
 *
 * À esquerda, as peneiras (e, com sedimentação, as leituras do densímetro)
 * da repetição, cada linha ao lado do que passou na inicial; à direita, a
 * curva inicial tracejada sob a da repetição, com o ponto da inicial em que
 * a curva sobe marcado. No topo, as frações da NBR 6502 inicial × repetição
 * × diferença — todas do servidor (`vw_ensaio_granulometria` na vista de
 * cada tentativa); a tela só subtrai os dois números já expressos.
 *
 * Mt, Mu, «com sedimentação», o densímetro e a ρs transcrita são do
 * cabeçalho, comuns às tentativas: pelo caminho de quem interpreta eles vão
 * com o valor gravado (a API recusa outro, 403). A umidade higroscópica da
 * repetição se lança na etapa Umidade, que com a repetição aberta grava nela.
 */

// ══ as frações ════════════════════════════════════════════════════════

interface Fracao { chave: string; rotulo: string; nota: string; valor(c: any): unknown }

/** As frações que a ficha já mostra, pelas divisórias da NBR 6502. */
function fracoesDe(c: any): Fracao[] {
  const base: Fracao[] = [
    { chave: 'pedregulho', rotulo: 'Pedregulho', nota: '> 2,0 mm', valor: (x) => x?.fracao_pedregulho },
    { chave: 'areia_grossa', rotulo: 'Areia grossa', nota: '0,6 – 2,0 mm', valor: (x) => x?.fracao_areia_grossa },
    { chave: 'areia_media', rotulo: 'Areia média', nota: '0,2 – 0,6 mm', valor: (x) => x?.fracao_areia_media },
  ];
  if (!c?.com_sedimentacao) {
    return [...base, {
      chave: 'finos', rotulo: `Passa na ${numero(c?.abertura_minima, 3)} mm`,
      nota: 'silte e argila não determinados', valor: (x) => x?.finos_abaixo_da_ultima_peneira,
    }];
  }
  return [...base,
    { chave: 'areia_fina', rotulo: 'Areia fina', nota: '0,06 – 0,2 mm', valor: (x) => x?.fracao_areia_fina },
    { chave: 'silte', rotulo: 'Silte', nota: '0,002 – 0,06 mm', valor: (x) => x?.fracao_silte },
    { chave: 'argila', rotulo: 'Argila', nota: '< 0,002 mm', valor: (x) => x?.fracao_argila },
  ];
}

/** O percentual nas casas do laudo, ou `null`. */
const emPct = (v: unknown) => (v == null || v === ''
  ? null : Number((Number(v) * 100).toFixed(CASAS.fracao)));

/**
 * As frações inicial × repetição × diferença (NBR 6502). Com `atual` nulo
 * (as tentativas anteriores), só a coluna da tentativa.
 */
export function FracoesLadoALado({ inicial, atual, vigente, numero: n, provisorio }: {
  inicial: EnsaioGranulometria | null; atual: EnsaioGranulometria | null;
  vigente?: number; numero?: number; provisorio?: boolean;
}) {
  const ci = inicial?.cabecalho;
  const ca = atual?.cabecalho;
  const lista = fracoesDe(ca ?? ci);
  const dois = atual != null;
  const colunas: ColunaDeLancamento[] = dois
    ? [{ texto: 'Fração' }, { texto: `Inicial · tentativa ${vigente ?? ''} (%)`, num: true },
      { texto: `Repetição · tentativa ${n ?? ''}${provisorio ? ' (provisório)' : ''} (%)`, num: true },
      { texto: 'Diferença (p.p.)', num: true }]
    : [{ texto: 'Fração' }, { texto: 'Passa / fração (%)', num: true }];
  const linhas: LinhaDeLancamento[] = lista.map((f) => {
    const i = emPct(f.valor(ci));
    const a = emPct(f.valor(ca));
    const dif = i != null && a != null ? diferencaComSinal(a - i, CASAS.fracao) : '—';
    const celI = <span className="n" data-fracao={f.chave} data-lado="inicial">{pct(f.valor(ci))}</span>;
    return {
      chave: f.chave,
      titulo: <>{f.rotulo} <small className="sub">{f.nota}</small></>,
      resultado: dois ? <span className="n">{dif}</span> : celI,
      semCartao: [0],
      celulas: dois
        ? [<>{f.rotulo} <small className="sub">{f.nota}</small></>, celI,
          <span className="n" data-fracao={f.chave} data-lado="atual">{pct(f.valor(ca))}</span>,
          <b className="n" data-fracao={f.chave} data-lado="diferenca">{dif}</b>]
        : [<>{f.rotulo} <small className="sub">{f.nota}</small></>, celI],
    };
  });
  return (
    <BlocoDaFicha titulo={dois ? 'Frações · inicial × repetição' : 'Frações'}
      sub={dois
        ? `ABNT NBR 6502, pelas contas do banco em cada tentativa.${provisorio
          ? ' A repetição está provisória enquanto houver pendência.' : ''}`
        : 'ABNT NBR 6502.'}>
      <div className="fe-tabela">
        <TabelaDeLancamento rotulo="Frações granulométricas" classe="fe-lanc repeticao-fracoes"
          colunas={colunas} linhas={linhas} />
      </div>
    </BlocoDaFicha>
  );
}

// ══ a tela ════════════════════════════════════════════════════════════

const chaveDe = (fase: string, abertura: unknown) => `${fase}:${Number(abertura)}`;

export function RepeticaoGranulometria({ id, congelado, opcoesPeneira, aoMudar, children }: {
  id: string;
  congelado: boolean;
  opcoesPeneira: Opcao[];
  aoMudar(): void | Promise<void>;
  /** A Interpretação de sempre (o resultado e a curva do laudo). */
  children: ReactNode;
}) {
  const { pode } = useSessao();
  const podeInterpretar = pode('ensaio.interpretar');
  /* R1: com a repetição aberta, quem interpreta também lança nela. */
  const lanca = pode('ensaio.lancar') || podeInterpretar;
  const rep = useRepeticao<EnsaioGranulometria>(id, 'granulometria', aoMudar);
  const [retidas, setRetidas] = useState<Record<string, string>>({});
  const [peneiraDe, setPeneiraDe] = useState<Record<string, string>>({});
  const [leituras, setLeituras] = useState<Record<number, string>>({});
  const [temps, setTemps] = useState<Record<number, string>>({});
  const [mostrarInicial, setMostrarInicial] = useState(true);
  const [serieInteira, setSerieInteira] = useState(false);
  const [salvando, setSalvando] = useState<string | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const codigos = useMemo(() => opcoesPeneira.map(comoCodigo), [opcoesPeneira]);

  /* O que acabou de gravar sai do rascunho logo (`limpar`), e o botão se
     solta antes das releituras (Tasks 4–5). */
  const executar = async (marca: string, chamada: () => Promise<unknown>, r: UseRepeticao,
    limpar: () => void) => {
    setSalvando(marca); setErro(null);
    try {
      await chamada();
      limpar();
      setSalvando(null);
      await r.recarregar();
      void Promise.resolve(aoMudar()).catch(() => undefined);
    } catch (e) { setErro(e); } finally { setSalvando(null); }
  };

  // ── as peneiras ──
  const lancamentoPeneiras = (r: UseRepeticao<EnsaioGranulometria>) => {
    const ini = r.dados?.inicial;
    const atual = r.dados?.atual;
    const serie = [...(ini?.serie ?? atual?.serie ?? [])]
      .sort((a, b) => (a.fase === b.fase ? Number(b.abertura_mm) - Number(a.abertura_mm)
        : a.fase === 'grossa' ? -1 : 1));
    const daTentativa = (f: EnsaioGranulometria | null | undefined, s: any) =>
      (f?.peneiras ?? []).find((p) => p.fase === s.fase && Number(p.abertura_mm) === Number(s.abertura_mm));
    /* Só as peneiras usadas numa das tentativas (a série tem 25): a série
       inteira, para quem precisa de outra, fica a um clique. */
    const usadas = serie.filter((s) => daTentativa(ini, s) || daTentativa(atual, s)
      || retidas[chaveDe(s.fase, s.abertura_mm)]);
    const visiveis = serieInteira || !usadas.length ? serie : usadas;
    const anomalas = new Set(pontosAnomalos(pontosDaCurvaGranulometrica(ini).pen).map((p) => p.x));
    const valorDe = (s: any) => {
      const k = chaveDe(s.fase, s.abertura_mm);
      const g = daTentativa(atual, s);
      return retidas[k] ?? (g ? paraCampo(g.massa_retida) : '');
    };
    const peneiraUsada = (s: any) => {
      const k = chaveDe(s.fase, s.abertura_mm);
      return peneiraDe[k] ?? daTentativa(atual, s)?.peneira_equipamento_id ?? '';
    };
    /* Sobe só a peneira que mudou, como na bancada. */
    const mudadas = serie.map((s) => {
      const g = daTentativa(atual, s);
      const massa = paraNumero(valorDe(s));
      const eq = peneiraUsada(s) || undefined;
      const igual = g && massa != null && Math.abs(Number(g.massa_retida) - massa) < 1e-9
        && (g.peneira_equipamento_id ?? undefined) === eq;
      return igual || massa == null ? null
        : { fase: s.fase, aberturaMm: Number(s.abertura_mm), massaRetida: massa, peneiraEquipamentoId: eq };
    }).filter((x): x is NonNullable<typeof x> => x !== null);
    const falta = mudadas.length ? null : 'Digite a massa retida de pelo menos uma peneira.';

    const colunas: ColunaDeLancamento[] = [
      { texto: 'Peneira' },
      { texto: 'Inicial · passa (%)', num: true },
      { texto: 'Retida (g)', num: true },
      { texto: 'Peneira usada' },
      { texto: 'Repetição · passa (%)', num: true },
    ];
    const linhas: LinhaDeLancamento[] = visiveis.map((s) => {
      const k = chaveDe(s.fase, s.abertura_mm);
      const pi = daTentativa(ini, s);
      const pa = daTentativa(atual, s);
      const anomala = pi && anomalas.has(Number(s.abertura_mm));
      return {
        chave: k,
        classe: pa ? undefined : 'nova',
        titulo: <>{s.designacao} <small className="sub">{s.fase}</small></>,
        resultado: pa?.percentual_passa != null
          ? <span className="n">{pct(pa.percentual_passa)} %</span> : undefined,
        nota: pi ? `inicial ${pct(pi.percentual_passa)} %` : undefined,
        semCartao: [0, 1, 4],
        celulas: [
          <>{s.designacao} <small className="sub">{s.fase === 'grossa' ? 'grosso' : 'fino'}</small></>,
          pi ? <>{pct(pi.percentual_passa)}{anomala
            ? <> <Selo tom="atencao" titulo="Passa mais que a peneira acima dela">sobe</Selo></> : null}</>
            : '—',
          lanca ? (
            <CampoNumerico rotulo={`Massa retida na ${s.designacao} — repetição (g)`} rotuloOculto
              value={valorDe(s)} disabled={salvando !== null}
              onChange={(e) => setRetidas((m) => ({ ...m, [k]: e.target.value }))} />
          ) : pa ? numero(pa.massa_retida, 2) : '—',
          lanca ? (
            <EscolhaPorCodigo rotulo={`Peneira usada na ${s.designacao} — repetição`} rotuloOculto
              opcoes={codigos} valor={peneiraUsada(s)}
              aoEscolher={(v) => setPeneiraDe((m) => ({ ...m, [k]: v }))} />
          ) : <span className="cod">{codigos.find((o) => o.valor === pa?.peneira_equipamento_id)
            ?.codigo ?? '—'}</span>,
          pa ? <b className="fe-w">{pct(pa.percentual_passa)}</b> : '—',
        ],
      };
    });
    return (
      <BlocoDaFicha titulo={`Peneiras · tentativa ${r.dados?.aberta?.numero ?? ''}`}
        sub="Massa retida individual, como na bancada. Ao lado, o que passou em cada peneira na inicial; «sobe» marca a peneira em que a curva inicial subiu.">
        <div className="fe-tabela">
          {usadas.length > 0 && usadas.length < serie.length && (
            <div className="fe-tabela-topo">
              <h3>{serieInteira ? 'A série inteira' : 'As peneiras usadas'}
                {' '}<small>{visiveis.length} de {serie.length}</small></h3>
              <button type="button" className="botao discreto pequeno" aria-pressed={serieInteira}
                onClick={() => setSerieInteira((v) => !v)}>
                {serieInteira ? 'Só as usadas' : 'Mostrar a série inteira'}
              </button>
            </div>
          )}
          <TabelaDeLancamento rotulo="Peneiras da repetição — inicial e repetição lado a lado"
            classe="fe-lanc repeticao-lanc repeticao-peneiras" colunas={colunas} linhas={linhas} />
          {lanca && (
            <div className="acoes fe-acoes">
              {falta ? <p className="o-que-falta">{falta}</p>
                : <p className="fe-lembrete">Sobe só a peneira que mudou; a em branco fica de fora.</p>}
              <button type="button" className="botao secundario" data-gravar-tabela=""
                aria-disabled={falta !== null || salvando !== null}
                title={falta ?? 'Grava as peneiras na repetição'}
                onClick={() => {
                  if (falta || salvando !== null) return;
                  void executar('peneiras', () => gravarGranulometriaPeneiras(id, { peneiras: mudadas }),
                    r, () => { setRetidas({}); setPeneiraDe({}); });
                }}>
                {salvando === 'peneiras' ? 'Gravando…' : 'Gravar peneiras'}
              </button>
            </div>
          )}
        </div>
      </BlocoDaFicha>
    );
  };

  // ── a sedimentação ──
  const lancamentoSedimentacao = (r: UseRepeticao<EnsaioGranulometria>) => {
    const ini = r.dados?.inicial;
    const atual = r.dados?.atual;
    const c = atual?.cabecalho ?? ini?.cabecalho;
    if (!c?.com_sedimentacao) return null;
    const vivas = (f: EnsaioGranulometria | null | undefined) =>
      (f?.sedimentacao ?? []).filter((x) => !x.descartada);
    const tempos = Array.from(new Set([...TEMPOS_DA_NORMA,
      ...vivas(ini).map((x) => Number(x.tempo_s)), ...vivas(atual).map((x) => Number(x.tempo_s))]))
      .sort((a, b) => a - b);
    const daTentativa = (f: EnsaioGranulometria | null | undefined, t: number) =>
      vivas(f).find((x) => Number(x.tempo_s) === t);
    const leituraDe = (t: number) => leituras[t]
      ?? (daTentativa(atual, t) ? paraCampo(daTentativa(atual, t).leitura) : '');
    const tempDe = (t: number) => temps[t]
      ?? (daTentativa(atual, t)?.temperatura != null ? paraCampo(daTentativa(atual, t).temperatura) : '');
    const mudadas = tempos.map((t) => {
      const g = daTentativa(atual, t);
      const l = paraNumero(leituraDe(t));
      const tp = paraNumero(tempDe(t));
      const igual = g && l != null && Number(g.leitura) === l
        && (g.temperatura == null ? tp == null : Number(g.temperatura) === tp);
      return igual || l == null ? null : { tempoS: t, leitura: l, ...(tp != null ? { temperatura: tp } : {}) };
    }).filter((x): x is NonNullable<typeof x> => x !== null);
    const falta = mudadas.length ? null : 'Digite pelo menos uma leitura do densímetro.';
    const passa = (x: any) => (x?.situacao === 'ok' ? <b className="fe-w">{pct(x.percentual_passa)}</b>
      : x ? <Selo tom="atencao">{SITUACAO_LEITURA[x.situacao] ?? x.situacao}</Selo> : '—');

    const colunas: ColunaDeLancamento[] = [
      { texto: 'Tempo' },
      { texto: 'Inicial · passa (%)', num: true },
      { texto: 'Leitura L (g/cm³)', num: true },
      { texto: 'T (°C)', num: true },
      { texto: 'Repetição · passa (%)', num: true },
    ];
    const linhas: LinhaDeLancamento[] = tempos.map((t, i) => {
      const xi = daTentativa(ini, t);
      const xa = daTentativa(atual, t);
      return {
        chave: String(t),
        classe: xa ? undefined : 'nova',
        titulo: rotuloTempo(t),
        resultado: xa?.situacao === 'ok' ? <span className="n">{pct(xa.percentual_passa)} %</span> : undefined,
        nota: xi?.situacao === 'ok' ? `inicial ${pct(xi.percentual_passa)} %` : undefined,
        semCartao: [0, 1, 4],
        celulas: [
          rotuloTempo(t),
          xi?.situacao === 'ok' ? pct(xi.percentual_passa) : '—',
          lanca ? <CampoNumerico rotulo={`Leitura aos ${rotuloTempo(t)} — repetição`} rotuloOculto
            value={leituraDe(t)} disabled={salvando !== null}
            onChange={(e) => setLeituras((m) => ({ ...m, [t]: e.target.value }))} />
            : xa ? numero(xa.leitura, 4) : '—',
          lanca ? <CampoNumerico rotulo={`Temperatura aos ${rotuloTempo(t)} — repetição (°C)`} rotuloOculto
            value={tempDe(t)} placeholder={i < 2 ? 'medida depois' : undefined}
            disabled={salvando !== null}
            onChange={(e) => setTemps((m) => ({ ...m, [t]: e.target.value }))} />
            : xa?.temperatura != null ? numero(xa.temperatura, 1) : '—',
          passa(xa),
        ],
      };
    });
    return (
      <BlocoDaFicha titulo={`Sedimentação · tentativa ${r.dados?.aberta?.numero ?? ''}`}
        sub="O densímetro, «com sedimentação» e a ρs transcrita são do cabeçalho, os mesmos da inicial. A leitura vira número pela curva do densímetro, no banco.">
        <div className="fe-tabela">
          <TabelaDeLancamento rotulo="Leituras do densímetro da repetição"
            classe="fe-lanc repeticao-lanc sedimentacao" colunas={colunas} linhas={linhas} />
          {lanca && (
            <div className="acoes fe-acoes">
              {falta ? <p className="o-que-falta">{falta}</p>
                : <p className="fe-lembrete">Relançar um tempo substitui a leitura dele na repetição.</p>}
              <button type="button" className="botao secundario" data-gravar-tabela=""
                aria-disabled={falta !== null || salvando !== null}
                title={falta ?? 'Lança as leituras na repetição'}
                onClick={() => {
                  if (falta || salvando !== null) return;
                  /* O cabeçalho vai como está gravado: pelo caminho de quem
                     interpreta, outro valor é recusado (403). */
                  void executar('sedimentacao', () => gravarGranulometriaSedimentacao(id, {
                    comSedimentacao: !!c.com_sedimentacao,
                    ...(c.densimetro_equipamento_id
                      ? { densimetroEquipamentoId: c.densimetro_equipamento_id } : {}),
                    ...(c.massa_especifica_informada != null
                      ? { massaEspecificaInformada: Number(c.massa_especifica_informada) } : {}),
                    leituras: mudadas,
                  }), r, () => { setLeituras({}); setTemps({}); });
                }}>
                {salvando === 'sedimentacao' ? 'Gravando…' : 'Gravar leituras'}
              </button>
            </div>
          )}
        </div>
      </BlocoDaFicha>
    );
  };

  const lancamento = (r: UseRepeticao<EnsaioGranulometria>) => (
    <>
      {lancamentoPeneiras(r)}
      {lancamentoSedimentacao(r)}
      <p className="sub repeticao-dica">
        {lanca
          ? 'A umidade higroscópica da repetição se lança na etapa Umidade (as cápsulas vão para a repetição); Mt e Mu são do cabeçalho, comuns às tentativas.'
          : 'Lançar pede a permissão de lançar dados de ensaio: as peneiras chegam aqui quando o laboratório as lança na bancada.'}
      </p>
      <ErroDaApi erro={erro} />
    </>
  );

  const grafico = (r: UseRepeticao<EnsaioGranulometria>) => (r.dados?.atual ? (
    <CurvaGranulometrica d={r.dados.atual} inicial={mostrarInicial ? r.dados.inicial : null}
      titulo="Curva granulométrica · inicial × repetição"
      acao={(
        <label className="marcavel">
          <input type="checkbox" checked={mostrarInicial}
            onChange={(e) => setMostrarInicial(e.target.checked)} />
          mostrar a inicial
        </label>
      )} />
  ) : null);

  const quadro = (r: UseRepeticao<EnsaioGranulometria>) => (
    <FracoesLadoALado inicial={r.dados?.inicial ?? null} atual={r.dados?.atual ?? null}
      vigente={r.dados?.vigente} numero={r.dados?.aberta?.numero}
      provisorio={(r.dados?.pendenciasDaRepeticao.length ?? 0) > 0} />
  );

  return (
    <MolduraDaRepeticao ensaioId={id} ficha="granulometria" titulo="Granulometria"
      podeInterpretar={podeInterpretar} congelado={congelado} repeticao={rep} aoMudar={aoMudar}
      frase={<>Cada peneira e cada leitura do densímetro da repetição entra na curva assim que
        é lançada. O laudo continua com a curva inicial até você adotar a repetição.</>}
      quadro={quadro} lancamento={lancamento} grafico={grafico}>
      {children}
    </MolduraDaRepeticao>
  );
}
